/**
 * Client-side sim tick driver (v0M3.7b).
 *
 * Consumes wall-clock `dt` from the `requestAnimationFrame` loop and
 * calls `runTick` at the right cadence. Tick cadence is unchanged at
 * 5 ticks/wall-second at 1× (200 ms/tick at 1×; 100 ms at 2×; 50 ms at 4×);
 * MFix3.8 changed the game-day scale from 5 ticks/day to 25 ticks/day,
 * so 1 game-day at 1× = 5 wall-seconds (was 1 wall-second). Pause = don't tick.
 *
 * Fixed-cadence accumulator model — the driver banks wall-time into
 * `accumulatorMs`; whenever it exceeds one tick-interval-at-current-speed,
 * one tick fires and the accumulator drains by that interval. Multiple
 * ticks can fire in a single frame if the browser stalls (spike in a
 * background tab), keeping the game clock accurate.
 *
 * The driver holds a mutable `state` reference — `runTick` returns a
 * fresh GameState per call, so the driver swaps the reference. Callers
 * read `driver.getState()` each frame for the current authoritative
 * state (used by the renderer + speed HUD).
 *
 * Command queue is `driver.enqueue(cmd)` — commands accumulate per
 * frame + drain into the next tick call. Empty most frames until player
 * command UI (v0M3.7+) starts issuing move_army / build / recruit.
 */

import type { Command } from '../sim/commands';
import type { GameState } from '../sim/state';
import type { SimContext } from '../sim/sim-context';
import { runTick } from '../sim';

/** Speed values: 0 = pause, positive integers = tick multiplier. */
export type SimSpeed = 0 | 1 | 2 | 4;

/** How many wall-milliseconds elapse per tick at each speed. */
const MS_PER_TICK_AT_1X = 200; // 5 ticks/wall-second at 1× (unchanged in MFix3.8 — tick cadence stays; game-day scale grows to 25 ticks/day)

export interface SimDriver {
  /**
   * Advance the driver by `dtMs` of wall-clock time. Fires as many ticks
   * as the accumulator supports. No-op at speed 0. Clamps `dtMs` to
   * `MAX_CATCHUP_MS` to prevent a background-tab return from spiralling
   * through hundreds of catch-up ticks.
   */
  tick(dtMs: number): void;
  /** Read-only view of the current authoritative sim state. */
  getState(): GameState;
  /** Enqueue a command to be delivered on the next tick call. */
  enqueue(cmd: Command): void;
  /** Current playback speed. */
  getSpeed(): SimSpeed;
  /** Set playback speed. Returns the new speed (identity — for fluent chaining). */
  setSpeed(next: SimSpeed): SimSpeed;
  /**
   * Space-bar toggle: pause → last non-zero speed; non-zero → pause.
   * Returns the new speed. Default resume speed if no prior speed = 1.
   */
  togglePause(): SimSpeed;
  /**
   * How many game ticks have been executed since driver creation. Useful
   * for the game-day counter (day = tick // 5).
   */
  getTicksElapsed(): number;
}

/** Prevent unbounded catch-up if a browser tab was backgrounded for minutes. */
const MAX_CATCHUP_MS = 1000;

export function createSimDriver(args: {
  initialState: GameState;
  context: SimContext;
  initialSpeed?: SimSpeed;
}): SimDriver {
  let state = args.initialState;
  const context = args.context;
  let speed: SimSpeed = args.initialSpeed ?? 1;
  let lastActiveSpeed: SimSpeed = speed === 0 ? 1 : speed;
  let accumulatorMs = 0;
  let commands: Command[] = [];
  let ticksElapsed = 0;

  function tick(dtMs: number): void {
    if (speed === 0) {
      // Paused — don't accumulate. Prevents post-resume tick-burst.
      accumulatorMs = 0;
      return;
    }
    const dtClamped = Math.min(dtMs, MAX_CATCHUP_MS);
    accumulatorMs += dtClamped;
    const tickIntervalMs = MS_PER_TICK_AT_1X / speed;
    while (accumulatorMs >= tickIntervalMs) {
      const batch = commands;
      commands = [];
      state = runTick(state, batch, context);
      ticksElapsed++;
      accumulatorMs -= tickIntervalMs;
    }
  }

  function getState(): GameState {
    return state;
  }

  function enqueue(cmd: Command): void {
    commands.push(cmd);
  }

  function getSpeed(): SimSpeed {
    return speed;
  }

  function setSpeed(next: SimSpeed): SimSpeed {
    if (next !== 0) lastActiveSpeed = next;
    speed = next;
    return speed;
  }

  function togglePause(): SimSpeed {
    if (speed === 0) return setSpeed(lastActiveSpeed);
    return setSpeed(0);
  }

  function getTicksElapsed(): number {
    return ticksElapsed;
  }

  return {
    tick,
    getState,
    enqueue,
    getSpeed,
    setSpeed,
    togglePause,
    getTicksElapsed,
  };
}
